import { ReactNode } from 'react';
import { i18n } from '@lingui/core';
import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import { useCompany } from '@/models/company';
import { Entity } from '@/models/entity';
import { SUPPORTED_LOCALES } from '@/services/i18n';

export const OverviewSection = ({ entity }: { entity: Entity }) => {
  const { t } = useLingui();

  const languageOptions = SUPPORTED_LOCALES.map((locale) => ({
    id: locale.id,
    text: i18n._(locale.label),
  }));

  return (
    <Stack gap="1em">
      <Box style={{ flex: 0 }}>
        <Title as="h2" size="300" weight="medium" style={{ marginBottom: '16px' }}>
          <Trans id="settings.entities.overview.informations">Informations</Trans>
        </Title>
        {entity?.contactInfo?.phone?.number && (
          <FieldRow label={t({ id: 'settings.entities.contact.phone', message: 'Phone' })}>
            <InlineText>{`+${entity?.contactInfo?.phone?.countryCode} ${entity?.contactInfo?.phone?.number}`}</InlineText>
          </FieldRow>
        )}
        <FieldRow
          label={t({ id: 'settings.entities.contact.contact-name', message: 'Contact name' })}
        >
          <InlineText>{entity?.address?.contactName ?? '-'}</InlineText>
        </FieldRow>
        <FieldRow
          label={t({ id: 'settings.entities.contact.contact-email', message: 'Contact email' })}
        >
          <InlineText>{entity?.address?.contactEmail ?? '-'}</InlineText>
        </FieldRow>
        <FieldRow label={t({ id: 'settings.entities.language.label', message: 'Language' })}>
          <InlineText>
            {languageOptions.find((languageOption) => entity?.defaultLocale === languageOption.id)
              ?.text ?? '-'}
          </InlineText>
        </FieldRow>
        {entity?.companyId && <CompanyRow companyId={entity.companyId} />}
      </Box>
    </Stack>
  );
};

const FieldRow = ({ label, children }: { label?: string | null; children?: ReactNode }) => (
  <Stack row padding="0 0 0 2em" key={label}>
    <InlineText weight="medium" style={{ width: '20%' }}>
      {label}
    </InlineText>
    <InlineText style={{ width: '80%' }}>{children}</InlineText>
  </Stack>
);

const CompanyRow = ({ companyId }: { companyId: string }) => {
  const { t } = useLingui();
  const { data: company } = useCompany(companyId);

  if (!company) return null;

  return (
    <FieldRow label={t({ id: 'settings.entities.company.label', message: 'Company' })}>
      <Button variant="link-subtle" to={`/settings_beta/billing/companies/${companyId}`}>
        <InlineText>{company.name}</InlineText>
      </Button>
    </FieldRow>
  );
};
